import { useCallback, useState } from 'react';
import {
  Badge,
  Alert,
  AlertDescription,
  Spinner,
  Button,
  Input,
} from '@klaro7/ui';
import type { QuestionnaireSession, QuestionnaireStatus, QuestionnaireAnswers } from '@klaro7/shared-types';

import {
  useAdminSessions,
  useAdminSession,
  useApproveSession,
  useRejectSession,
  useSaveFieldRatings,
} from '@/features/questionnaire/hooks/useQuestionnaireAdmin';
import { extractErrorMessage } from '@/lib/api';
import { QuestionnaireStructure } from '@/features/questionnaire/components/QuestionnaireStructure';

const STATUS_LABELS: Record<QuestionnaireStatus, string> = {
  in_progress: 'In Corso',
  completed: 'Completato',
  approved: 'Approvato',
  rejected: 'Rifiutato',
};

const STATUS_BADGE_VARIANT: Record<QuestionnaireStatus, 'warning' | 'info' | 'success' | 'error'> = {
  in_progress: 'warning',
  completed: 'info',
  approved: 'success',
  rejected: 'error',
};

const STEP_NAMES: Record<number, string> = {
  1: 'Candidatura Rapida',
  2: 'Profilo Professionale',
  3: 'Stile e Gestione',
  4: 'Aspetti Pratici',
  5: 'Affidabilita',
  6: 'Scenari',
  7: 'Motivazione',
  8: 'Prova Pratica',
  9: 'GDPR',
};

const FIELD_LABELS: Record<string, string> = {
  nome: 'Nome', cognome: 'Cognome', email: 'Email', telefono: 'Telefono',
  citta: 'Citta', regione: 'Regione', lingue: 'Lingue', esperienza: 'Esperienza',
  strumenti: 'Strumenti', ambiti: 'Ambiti', situazioneLavorativa: 'Situazione Lavorativa',
  partitaIva: 'Partita IVA', modalitaConsulenza: 'Modalita Consulenza',
  disponibilitaSettimanale: 'Disponibilita Settimanale', foto: 'Foto',
  titoloDiStudio: 'Titolo di Studio', ultimaOccupazione: 'Ultima Occupazione',
  praticaDa: 'Pratica Da', corsiOAutodidatta: 'Corsi o Autodidatta',
  attestati: 'Attestati', piattaformeOnline: 'Piattaforme Online',
  stileDiLettura: 'Stile di Lettura', durataMediaConsulto: 'Durata Media Consulto',
  connessioneStabile: 'Connessione Stabile', gestioneStress: 'Gestione Stress',
  temiNonTrattati: 'Temi Non Trattati', riservatezza: 'Riservatezza',
  gestioneRichiesteDelicate: 'Gestione Richieste Delicate',
  giorniFasceOrarie: 'Giorni e Fasce Orarie', disponibileTurni: 'Disponibile a Turni',
  partitaIvaDettaglio: 'Partita IVA (Dettaglio)', accettazioneNDA: 'Accettazione NDA',
  accettazioneRegolamento: 'Accettazione Regolamento',
  oreSettimanaliGarantite: 'Ore Settimanali Garantite',
  disponibileOnlineSenzaConsulti: 'Disponibile Online Senza Consulti',
  gestioneImprevisti: 'Gestione Imprevisti', competenzeDigitali: 'Competenze Digitali',
  rispostaChat2Min: 'Risposta Chat 2 Min', lamenteleRicevute: 'Lamentele Ricevute',
  scenario1: 'Scenario 1', scenario2: 'Scenario 2', scenario3: 'Scenario 3',
  puntiDiForza: 'Punti di Forza', puntiDeboli: 'Punti Deboli', migliorare: 'Migliorare',
  percheKlaro7: 'Perche Klaro7', obiettivi: 'Obiettivi', cosaTiDistingue: 'Cosa Ti Distingue',
  disponibileProvaLettura: 'Disponibile Prova Lettura', situazioneDifficile: 'Situazione Difficile',
  accettaPrivacy: 'Accetta Privacy', accettaNDA: 'Accetta NDA', accettaRegolamento: 'Accetta Regolamento',
};

type StatusFilter = QuestionnaireStatus | 'all';

function formatValue(value: unknown): string {
  if (value === null || value === undefined) return '-';
  if (typeof value === 'boolean') return value ? 'Si' : 'No';
  if (typeof value === 'number') return String(value);
  if (Array.isArray(value)) {
    return value
      .map((item) => {
        if (typeof item === 'object' && item !== null) {
          if ('lingua' in item && 'livello' in item) {
            return `${item.lingua} (${item.livello})`;
          }
          return JSON.stringify(item);
        }
        return String(item);
      })
      .join(', ');
  }
  return String(value);
}

function formatDate(date: string | Date): string {
  return new Date(date).toLocaleDateString('it-IT', {
    day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

function PhotoPreview({ url }: { url: string }) {
  const [failed, setFailed] = useState(false);

  if (failed) {
    return (
      <a href={url} target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:underline break-all text-xs">
        {url}
      </a>
    );
  }

  return (
    <a href={url} target="_blank" rel="noopener noreferrer" className="inline-block">
      <img
        src={url}
        alt="Foto profilo"
        crossOrigin="anonymous"
        className="h-24 w-24 rounded-lg object-cover border border-neutral-200 hover:opacity-90 transition-opacity"
        onError={() => setFailed(true)}
      />
    </a>
  );
}

const STAR_PATH = 'M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z';

/** Single star that can be empty, half, or full — uses clipPath for half-star, no gradient IDs */
function Star({ filled }: { filled: 'empty' | 'half' | 'full' }) {
  return (
    <svg className="h-6 w-6" viewBox="0 0 20 20">
      {/* Background: always gray */}
      <path fill="#E5E7EB" d={STAR_PATH} />
      {/* Foreground: orange, clipped for half */}
      {filled !== 'empty' && (
        <path
          fill="#EB7223"
          d={STAR_PATH}
          clipPath={filled === 'half' ? 'inset(0 50% 0 0)' : undefined}
          style={{ transition: 'fill 0.1s ease' }}
        />
      )}
    </svg>
  );
}

/** Interactive star rating with half-star support (0.5 to 5) */
function InteractiveStarRating({ value, onChange }: { value: number; onChange: (v: number) => void }) {
  const [hover, setHover] = useState<number | null>(null);
  const display = hover ?? value;

  return (
    <div
      className="flex items-center gap-1"
      onMouseLeave={() => setHover(null)}
    >
      {[1, 2, 3, 4, 5].map((star) => {
        const filled: 'empty' | 'half' | 'full' =
          display >= star ? 'full' : display >= star - 0.5 ? 'half' : 'empty';

        return (
          <div key={star} className="relative h-6 w-6 cursor-pointer">
            {/* Left half → half star */}
            <div
              className="absolute inset-y-0 left-0 w-1/2 z-10"
              onMouseEnter={() => setHover(star - 0.5)}
              onClick={() => onChange(star - 0.5)}
            />
            {/* Right half → full star */}
            <div
              className="absolute inset-y-0 right-0 w-1/2 z-10"
              onMouseEnter={() => setHover(star)}
              onClick={() => onChange(star)}
            />
            <Star filled={filled} />
          </div>
        );
      })}
      <span className={`ml-1.5 min-w-[2rem] text-xs font-bold tabular-nums transition-opacity duration-150 ${
        display > 0 ? 'text-brand-orange opacity-100' : 'text-neutral-300 opacity-0'
      }`}>
        {display > 0 ? display.toFixed(1) : '0.0'}
      </span>
    </div>
  );
}

/** Determines if a field value looks like a free-text answer that can be rated */
function isRateableField(key: string, value: unknown): boolean {
  if (typeof value !== 'string') return false;
  // Skip short values, booleans-as-strings, URLs, emails, phones
  if (value.length < 15) return false;
  if (['si', 'no', 'dipende', 'da_valutare'].includes(value)) return false;
  if (value.startsWith('http')) return false;
  if (value.includes('@') && value.includes('.')) return false;
  // Skip personal data fields
  const personalFields = ['nome', 'cognome', 'email', 'telefono', 'citta', 'regione', 'foto', 'attestati'];
  if (personalFields.includes(key)) return false;
  return true;
}

function SessionDetail({ sessionId, onClose }: { sessionId: string; onClose: () => void }) {
  const { data: session, isLoading, isError } = useAdminSession(sessionId);
  const approveMutation = useApproveSession();
  const rejectMutation = useRejectSession();
  const saveRatingsMutation = useSaveFieldRatings();
  const [rejectReason, setRejectReason] = useState('');
  const [approvalNotes, setApprovalNotes] = useState('');
  const [showRejectInput, setShowRejectInput] = useState(false);
  const [showApproveInput, setShowApproveInput] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [localRatings, setLocalRatings] = useState<Record<string, { rating: number; comment?: string }>>({});
  const [ratingsInitialized, setRatingsInitialized] = useState(false);
  const [ratingsSaved, setRatingsSaved] = useState(false);

  const handleApprove = async () => {
    if (!approvalNotes.trim()) return;
    setActionError(null);
    try {
      await approveMutation.mutateAsync({ id: sessionId, approvalNotes: approvalNotes.trim() });
      onClose();
    } catch (error) {
      setActionError(extractErrorMessage(error));
    }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) return;
    setActionError(null);
    try {
      await rejectMutation.mutateAsync({ id: sessionId, reason: rejectReason.trim() });
      onClose();
    } catch (error) {
      setActionError(extractErrorMessage(error));
    }
  };

  // Initialize local ratings from server data
  if (session && !ratingsInitialized) {
    const serverRatings = (session.fieldRatings ?? {}) as Record<string, { rating: number; comment?: string }>;
    if (Object.keys(serverRatings).length > 0) {
      setLocalRatings(serverRatings);
    }
    setRatingsInitialized(true);
  }

  const updateFieldRating = useCallback((fieldKey: string, rating: number) => {
    setLocalRatings((prev) => ({
      ...prev,
      [fieldKey]: { ...prev[fieldKey], rating },
    }));
    setRatingsSaved(false);
  }, []);

  const updateFieldComment = useCallback((fieldKey: string, comment: string) => {
    setLocalRatings((prev) => ({
      ...prev,
      [fieldKey]: { rating: prev[fieldKey]?.rating || 0, comment: comment || undefined },
    }));
    setRatingsSaved(false);
  }, []);

  const handleSaveRatings = async () => {
    // Filter out entries with no rating set
    const validRatings = Object.fromEntries(
      Object.entries(localRatings).filter(([, v]) => v.rating > 0)
    );
    if (Object.keys(validRatings).length === 0) return;
    try {
      await saveRatingsMutation.mutateAsync({ id: sessionId, ratings: validRatings });
      setRatingsSaved(true);
    } catch (error) {
      setActionError(extractErrorMessage(error));
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  if (isError || !session) {
    return (
      <Alert variant="error">
        <AlertDescription>Errore nel caricamento della sessione.</AlertDescription>
      </Alert>
    );
  }

  const answers = (session.answers ?? {}) as QuestionnaireAnswers;
  const completionPercent = session.status === 'completed' || session.status === 'approved'
    ? 100
    : Math.round(((session.currentStep - 1) / 9) * 100);

  return (
    <div className="space-y-6">
      {/* Back button + title */}
      <div className="flex items-center gap-4">
        <button
          onClick={onClose}
          aria-label="Torna alla lista"
          className="flex h-9 w-9 items-center justify-center rounded-lg border border-neutral-200 text-neutral-500 hover:bg-neutral-50 hover:text-neutral-700 transition-colors"
        >
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <div className="flex-1">
          <h2 className="text-lg font-bold text-neutral-900">
            {(answers[1]?.nome ?? '')} {(answers[1]?.cognome ?? '')}
          </h2>
          <p className="text-sm text-neutral-600">{answers[1]?.email ?? '-'}</p>
        </div>
        <Badge variant={STATUS_BADGE_VARIANT[session.status]}>
          {STATUS_LABELS[session.status]}
        </Badge>
      </div>

      {/* Info cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="rounded-xl border border-neutral-200 bg-white p-4">
          <p className="text-xs font-medium text-neutral-600 uppercase tracking-wider">Progresso</p>
          <div className="mt-2 flex items-center gap-2">
            <div className="flex-1 h-2 rounded-full bg-neutral-100 overflow-hidden">
              <div className="h-full rounded-full bg-brand-teal transition-all" style={{ width: `${completionPercent}%` }} />
            </div>
            <span className="text-sm font-bold text-neutral-700">{session.currentStep}/9</span>
          </div>
        </div>
        <div className="rounded-xl border border-neutral-200 bg-white p-4">
          <p className="text-xs font-medium text-neutral-600 uppercase tracking-wider">Creato</p>
          <p className="mt-2 text-sm font-semibold text-neutral-800">{formatDate(session.createdAt)}</p>
        </div>
        {session.completedAt && (
          <div className="rounded-xl border border-neutral-200 bg-white p-4">
            <p className="text-xs font-medium text-neutral-600 uppercase tracking-wider">Completato</p>
            <p className="mt-2 text-sm font-semibold text-neutral-800">{formatDate(session.completedAt)}</p>
          </div>
        )}
        <div className="rounded-xl border border-neutral-200 bg-white p-4">
          <p className="text-xs font-medium text-neutral-600 uppercase tracking-wider">Stato</p>
          <p className="mt-2">
            <Badge variant={STATUS_BADGE_VARIANT[session.status]}>{STATUS_LABELS[session.status]}</Badge>
          </p>
        </div>
      </div>

      {/* Answers by step */}
      <div className="overflow-hidden rounded-xl border border-neutral-200 bg-white">
        <div className="flex items-center justify-between border-b border-neutral-100 bg-neutral-50 px-5 py-3.5">
          <h3 className="text-sm font-semibold text-neutral-700">Risposte</h3>
          {(() => {
            const rated = Object.values(localRatings).filter((r) => r.rating > 0);
            if (rated.length === 0) return null;
            const avg = rated.reduce((sum, r) => sum + r.rating, 0) / rated.length;
            return (
              <div className="flex items-center gap-1.5">
                <span className="text-xs italic text-neutral-500">
                  AVG <span className="font-bold text-brand-orange">{avg.toFixed(1)}</span>
                  <span className="text-neutral-400"> / 5</span>
                </span>
                <div className="group relative">
                  <div className="flex h-4 w-4 cursor-help items-center justify-center rounded-full border border-neutral-300 text-[9px] font-bold text-neutral-400">
                    i
                  </div>
                  <div className="pointer-events-none absolute right-0 top-full z-20 mt-1.5 w-56 rounded-lg bg-neutral-900/95 px-3 py-2 text-[11px] text-white/80 opacity-0 shadow-lg transition-opacity group-hover:opacity-100">
                    Media calcolata su {rated.length} campo{rated.length !== 1 ? 'i' : ''} valutato{rated.length !== 1 ? 'i' : ''}
                  </div>
                </div>
              </div>
            );
          })()}
        </div>
        <div className="divide-y divide-neutral-100">
          {([1, 2, 3, 4, 5, 6, 7, 8, 9] as const).map((stepNum) => {
            const stepAnswers = answers[stepNum];
            if (!stepAnswers || Object.keys(stepAnswers).length === 0) return null;

            return (
              <details key={stepNum} className="group">
                <summary className="flex cursor-pointer items-center gap-3 px-5 py-3.5 hover:bg-neutral-50/50 transition-colors">
                  <svg className="h-4 w-4 text-neutral-400 transition-transform group-open:rotate-90" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                  <span className="text-sm font-semibold text-neutral-800">
                    Step {stepNum}
                  </span>
                  <span className="text-sm text-neutral-600">{STEP_NAMES[stepNum]}</span>
                </summary>
                <div className="bg-neutral-50/30 px-5 pb-4 pt-2">
                  <dl className="space-y-3">
                    {Object.entries(stepAnswers).map(([key, value]) => {
                      const fieldKey = `${stepNum}.${key}`;
                      const rateable = isRateableField(key, value);
                      const fieldRating = localRatings[fieldKey];

                      return (
                        <div key={key} className={`text-sm py-1 ${rateable ? 'rounded-lg border border-neutral-100 bg-white p-3' : ''}`}>
                          <div className="grid grid-cols-3 gap-2">
                            <dt className="font-medium text-neutral-600">
                              {FIELD_LABELS[key] || key}
                            </dt>
                            <dd className="col-span-2 text-neutral-800 whitespace-pre-wrap">
                              {key === 'foto' && typeof value === 'string' && value ? (
                                <PhotoPreview url={value} />
                              ) : key === 'attestati' && typeof value === 'string' && value ? (
                                <a href={value} target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:underline break-all">
                                  {value}
                                </a>
                              ) : (
                                formatValue(value)
                              )}
                            </dd>
                          </div>
                          {rateable && (
                            <div className="mt-2 flex flex-col gap-2 border-t border-neutral-100 pt-2">
                              <div className="flex items-center gap-3">
                                <span className="text-[11px] font-medium text-neutral-400 uppercase tracking-wider">Valutazione</span>
                                <InteractiveStarRating
                                  value={fieldRating?.rating || 0}
                                  onChange={(r) => updateFieldRating(fieldKey, r)}
                                />
                              </div>
                              <input
                                type="text"
                                value={fieldRating?.comment || ''}
                                onChange={(e) => updateFieldComment(fieldKey, e.target.value)}
                                placeholder="Commento (opzionale)..."
                                className="w-full rounded-md border border-neutral-200 bg-neutral-50 px-2.5 py-1.5 text-xs text-neutral-700 placeholder-neutral-400 focus:border-brand-teal focus:outline-none focus:ring-1 focus:ring-brand-teal"
                              />
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </dl>
                </div>
              </details>
            );
          })}
        </div>
      </div>

      {/* Save ratings button */}
      {Object.values(localRatings).some((r) => r.rating > 0) && (
        <div className="flex items-center gap-3">
          <Button
            onClick={handleSaveRatings}
            loading={saveRatingsMutation.isPending}
          >
            Salva Valutazioni
          </Button>
          {ratingsSaved && (
            <span className="text-sm font-medium text-emerald-600">Valutazioni salvate</span>
          )}
        </div>
      )}

      {actionError && (
        <Alert variant="error">
          <AlertDescription>{actionError}</AlertDescription>
        </Alert>
      )}

      {/* Approval/rejection notes */}
      {session.status === 'approved' && session.approvalNotes && (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4">
          <p className="text-xs font-medium text-emerald-700">Note di approvazione</p>
          <p className="mt-1 text-sm text-emerald-900">{session.approvalNotes}</p>
        </div>
      )}
      {session.status === 'rejected' && session.rejectionReason && (
        <div className="rounded-xl border border-red-200 bg-red-50 p-4">
          <p className="text-xs font-medium text-red-700">Motivo del rifiuto</p>
          <p className="mt-1 text-sm text-red-900">{session.rejectionReason}</p>
        </div>
      )}

      {/* Action buttons */}
      {session.status === 'completed' && (
        <div className="flex items-center gap-3 rounded-xl border border-neutral-200 bg-white p-5">
          {showRejectInput ? (
            <div className="flex-1 flex items-center gap-2">
              <Input
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Motivo del rifiuto..."
                className="flex-1"
              />
              <Button
                variant="danger"
                onClick={handleReject}
                disabled={!rejectReason.trim() || rejectMutation.isPending}
              >
                {rejectMutation.isPending ? 'Invio...' : 'Conferma'}
              </Button>
              <Button
                variant="outline"
                onClick={() => { setShowRejectInput(false); setRejectReason(''); }}
              >
                Annulla
              </Button>
            </div>
          ) : showApproveInput ? (
            <div className="flex w-full flex-col gap-2">
              <textarea
                value={approvalNotes}
                onChange={(e) => setApprovalNotes(e.target.value)}
                placeholder="Note di approvazione (obbligatorio)..."
                rows={3}
                className="w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:border-brand-teal focus:outline-none focus:ring-1 focus:ring-brand-teal"
              />
              <div className="flex gap-2">
                <Button
                  onClick={handleApprove}
                  disabled={!approvalNotes.trim() || approveMutation.isPending}
                >
                  {approveMutation.isPending ? 'Approvazione...' : 'Conferma Approvazione'}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => { setShowApproveInput(false); setApprovalNotes(''); }}
                >
                  Annulla
                </Button>
              </div>
            </div>
          ) : (
            <>
              <Button onClick={() => setShowApproveInput(true)}>
                Approva Questionario
              </Button>
              <Button variant="danger" onClick={() => setShowRejectInput(true)}>
                Rifiuta
              </Button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function SessionsTab() {
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(null);

  const { data, isLoading, isError } = useAdminSessions({
    page,
    limit: 20,
    status: statusFilter === 'all' ? undefined : statusFilter,
  });

  const handleStatusChange = (newStatus: StatusFilter) => {
    setStatusFilter(newStatus);
    setPage(1);
  };

  const sessions = data?.data ?? [];
  const pagination = data?.pagination;

  const statusFilters: { label: string; value: StatusFilter }[] = [
    { label: 'Tutti', value: 'all' },
    { label: 'In Corso', value: 'in_progress' },
    { label: 'Completati', value: 'completed' },
    { label: 'Approvati', value: 'approved' },
    { label: 'Rifiutati', value: 'rejected' },
  ];

  if (selectedSessionId) {
    return (
      <SessionDetail
        sessionId={selectedSessionId}
        onClose={() => setSelectedSessionId(null)}
      />
    );
  }

  return (
    <>
      {isError && (
        <Alert variant="error">
          <AlertDescription>Errore nel caricamento dei dati. Riprova più tardi.</AlertDescription>
        </Alert>
      )}

      {/* Table container */}
      <div className="overflow-hidden rounded-xl border border-neutral-200 bg-white">
        {/* Filter pills */}
        <div className="flex flex-wrap gap-2 border-b border-neutral-100 px-5 py-4">
          {statusFilters.map((filter) => (
            <button
              key={filter.value}
              onClick={() => handleStatusChange(filter.value)}
              aria-pressed={statusFilter === filter.value}
              className={`px-4 py-1.5 text-sm font-medium rounded-full transition-all ${
                statusFilter === filter.value
                  ? 'bg-brand-teal text-white shadow-sm'
                  : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'
              }`}
            >
              {filter.label}
            </button>
          ))}
        </div>

        {isLoading ? (
          <div className="flex justify-center py-12">
            <Spinner size="lg" />
          </div>
        ) : sessions.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-neutral-100">
              <svg className="h-6 w-6 text-neutral-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
              </svg>
            </div>
            <p className="mt-4 font-medium text-neutral-700">Nessun questionario trovato</p>
            <p className="mt-1 text-sm text-neutral-600">I questionari compilati appariranno qui</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-neutral-50">
                    <th className="px-5 py-3.5 text-left text-[11px] font-semibold uppercase tracking-wider text-neutral-700">Nome</th>
                    <th className="px-5 py-3.5 text-left text-[11px] font-semibold uppercase tracking-wider text-neutral-700">Email</th>
                    <th className="px-5 py-3.5 text-left text-[11px] font-semibold uppercase tracking-wider text-neutral-700">Stato</th>
                    <th className="px-5 py-3.5 text-left text-[11px] font-semibold uppercase tracking-wider text-neutral-700">Progresso</th>
                    <th className="px-5 py-3.5 text-left text-[11px] font-semibold uppercase tracking-wider text-neutral-700">Data</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-100">
                  {sessions.map((session: QuestionnaireSession) => {
                    const answers = (session.answers ?? {}) as QuestionnaireAnswers;
                    const name = `${answers[1]?.nome ?? ''} ${answers[1]?.cognome ?? ''}`.trim() || '-';
                    const filledEmail = answers[1]?.email;
                    // Until the questionnaire is filled, fall back to the address it
                    // was sent to (the linked user's email), shown italic + gray.
                    const sentToEmail =
                      session.userId && typeof session.userId === 'object' ? session.userId.email : undefined;
                    const progress = session.status === 'completed' || session.status === 'approved'
                      ? 100
                      : Math.round(((session.currentStep - 1) / 9) * 100);

                    return (
                      <tr
                        key={session.id}
                        role="button"
                        tabIndex={0}
                        onClick={() => setSelectedSessionId(session.id)}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSelectedSessionId(session.id); } }}
                        className="cursor-pointer hover:bg-neutral-50/50 transition-colors focus:outline-none focus:bg-brand-teal-50"
                      >
                        <td className="px-5 py-3.5 font-medium text-neutral-900">{name}</td>
                        <td className="px-5 py-3.5 text-sm">
                          {filledEmail ? (
                            <span className="text-neutral-600">{filledEmail}</span>
                          ) : sentToEmail ? (
                            <span
                              className="italic text-neutral-400"
                              title="Indirizzo a cui è stato inviato il questionario (non ancora compilato)"
                            >
                              {sentToEmail}
                            </span>
                          ) : (
                            <span className="text-neutral-600">-</span>
                          )}
                        </td>
                        <td className="px-5 py-3.5">
                          <Badge variant={STATUS_BADGE_VARIANT[session.status]}>
                            {STATUS_LABELS[session.status]}
                          </Badge>
                        </td>
                        <td className="px-5 py-3.5">
                          <div className="flex items-center gap-2">
                            <div className="w-20 h-1.5 rounded-full bg-neutral-100 overflow-hidden">
                              <div
                                className="h-full rounded-full bg-brand-teal"
                                style={{ width: `${progress}%` }}
                              />
                            </div>
                            <span className="text-xs text-neutral-600">{session.currentStep}/9</span>
                          </div>
                        </td>
                        <td className="px-5 py-3.5 text-sm text-neutral-600">
                          {formatDate(session.createdAt)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination && pagination.totalPages > 1 && (
              <div className="flex items-center justify-between border-t border-neutral-100 px-5 py-3.5">
                <p className="text-sm text-neutral-600">
                  Pagina {pagination.page} di {pagination.totalPages} ({pagination.total} totali)
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={!pagination.hasPrevPage}
                  >
                    Precedente
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage((p) => p + 1)}
                    disabled={!pagination.hasNextPage}
                  >
                    Successiva
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
}

export function AdminQuestionnairesPage() {
  const [activeTab, setActiveTab] = useState<'sessioni' | 'struttura'>('sessioni');

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div>
        <h1 className="text-2xl font-bold text-neutral-900">Questionari Professionisti</h1>
        <p className="mt-1 text-sm text-neutral-600">Gestisci e revisiona i questionari compilati dai professionisti</p>
      </div>

      {/* Tab buttons */}
      <div className="flex gap-2">
        <button
          onClick={() => setActiveTab('sessioni')}
          className={`px-4 py-1.5 text-sm font-medium rounded-full transition-all ${
            activeTab === 'sessioni'
              ? 'bg-brand-teal text-white shadow-sm'
              : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'
          }`}
        >
          Sessioni
        </button>
        <button
          onClick={() => setActiveTab('struttura')}
          className={`px-4 py-1.5 text-sm font-medium rounded-full transition-all ${
            activeTab === 'struttura'
              ? 'bg-brand-teal text-white shadow-sm'
              : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'
          }`}
        >
          Struttura
        </button>
      </div>

      {/* Tab content */}
      {activeTab === 'sessioni' ? <SessionsTab /> : <QuestionnaireStructure />}
    </div>
  );
}
